import {setOnboardingUsers} from '../slice/appSlice';
import {baseQueryWithReAuth} from './config';
import useVersionControlStore from '@/stores/version-control';
import {IOnboardingUser} from '@/types/user';
import {IRosterData, ISystemStatus} from '@/types/version-control';
import {createApi} from '@reduxjs/toolkit/query/react';

export const airlineAdminApi = createApi({
  reducerPath: 'airlineAdminApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: ['airlineAdmin'],
  endpoints: (builder) => ({
    getOnboardingUsers: builder.query<IOnboardingUser[], undefined>({
      query: () => ({
        url: '/ATLAS2k4_onboarding_user',
        method: 'GET'
      }),
      async onQueryStarted(_args, {dispatch, queryFulfilled}) {
        try {
          const data = await queryFulfilled;
          dispatch(setOnboardingUsers(data.data));
        } catch (error) {
          throw new Error('Error getOnboardingUsers');
        }
      }
    }),
    getOnboardingUserAirline: builder.mutation<
      {airlines: Record<string, string | null>},
      {userId: string}
    >({
      query: ({userId}) => ({
        url: `/ATLAS2k4_onboarding_user`,
        method: 'PUT',
        body: {
          action: 'user_airlines',
          list_uid: [userId]
        }
      })
    }),
    getOnboardingUsersProfile: builder.query<
      {profiles: Record<string, string | null>},
      {userIds: string[]}
    >({
      query: ({userIds}) => ({
        url: `/ATLAS2k4_onboarding_user`,
        method: 'PUT',
        body: {
          action: 'user_profiles',
          list_uid: userIds
        }
      })
    }),
    // ATLAS_roster_data_endpoint_RAM
    getRosterData: builder.mutation<
      IRosterData[],
      {
        airline: string;
        user_id: string;
        email: string;
        start_date: string;
        end_date: string;
      }
    >({
      query: (body) => ({
        url: '/ATLAS_roster_data_endpoint_RAM',
        method: 'POST',
        body
      })
    }),
    triggerAppFailure: builder.mutation<{status: string}, {status: string}>({
      query: (body) => ({
        url: '/ATLAS_trigger_failure_backend',
        method: 'POST',
        body
      })
    }),
    getAppFailureStatus: builder.query<ISystemStatus, {airline: string}>({
      query: (body) => ({
        url: '/ATLAS_trigger_failure_status',
        method: 'POST',
        body
      }),
      async onQueryStarted(_args, {queryFulfilled}) {
        try {
          const data = await queryFulfilled;
          if (data.data) {
            useVersionControlStore.getState().setAppFailureStatus(data.data);
          }
        } catch (error) {
          throw new Error('Error getAppFailureStatus');
        }
      }
    }),
    triggerSitmapFailure: builder.mutation<
      {status: string},
      {type: string; status: string}
    >({
      query: (body) => ({
        url: '/ATLAS_trigger_failure_sitmap',
        method: 'POST',
        body
      })
    }),
    triggerRosterFailure: builder.mutation<
      {status: string},
      {airline: string; status: string}
    >({
      query: (body) => ({
        url: '/ATLAS_trigger_failure_roster',
        method: 'POST',
        body
      })
    }),
    triggerFlightPlanFailure: builder.mutation<
      {status: string},
      {airline: string; status: string}
    >({
      query: (body) => ({
        url: '/ATLAS_trigger_failure_flight_plan',
        method: 'POST',
        body
      })
    }),
    getFlightPlanInfoExtract: builder.query<
      any,
      {
        airline: string;
        arr: string;
        dep: string;
        flight_number: string;
        sta: string;
        std: string;
      }
    >({
      query: (body) => ({
        url: '/ATLAS_get_flight_plan_info_extract_RAM',
        method: 'POST',
        body
      })
    }),
    getAirlineUsers: builder.query<
      IOnboardingUser[],
      {
        airline: string;
      }
    >({
      query: ({airline}) => ({
        url: `/ATLAS_airline_user?airline=${airline}`,
        method: 'GET'
      }),
      providesTags: ['airlineAdmin']
    }),
    createAirlineUser: builder.mutation<
      {message: string; status: string},
      {
        email: string;
        password: string;
        airline: string;
        name: string;
        rank: string;
        staff_id: string;
        fleet: string;
        role: string;
      }
    >({
      query: (body) => ({
        url: `/ATLAS_airline_user`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['airlineAdmin']
    }),
    deleteAirlineUser: builder.mutation<
      {message: string; status: string},
      {
        email: string;
      }
    >({
      query: ({email}) => ({
        url: `/ATLAS_airline_user?email=${email}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['airlineAdmin']
    }),
    updateAirlineUserBlockStatus: builder.mutation<
      {message: string; status: string},
      {
        uid: string;
        status: boolean;
      }
    >({
      query: ({uid, status}) => ({
        url: '/ATLAS_airline_user',
        method: 'PUT',
        body: {
          action: 'block',
          user_id: uid,
          block_status: status
        }
      }),
      invalidatesTags: ['airlineAdmin']
    }),
    getAirlineUserProfile: builder.query<
      {profiles: Record<string, string>},
      {
        listUid: string[];
      }
    >({
      query: ({listUid}) => ({
        url: '/ATLAS_airline_user',
        method: 'PUT',
        body: {
          action: 'user_profiles',
          list_uid: listUid
        }
      }),
      providesTags: ['airlineAdmin']
    }),
    updateAirlineUserProfile: builder.mutation<
      {message: string; status: string},
      {
        userId: string;
        email: string;
        password: string;
        airline: string;
        name: string;
        rank: string;
        staffId: string;
        fleet: string;
        role: string;
      }
    >({
      query: ({
        userId,
        email,
        password,
        airline,
        name,
        rank,
        staffId,
        fleet,
        role
      }) => ({
        url: '/ATLAS_airline_user',
        method: 'PUT',
        body: {
          action: 'update_user',
          user_id: userId,
          email,
          password,
          airline,
          name,
          rank,
          staff_id: staffId,
          fleet,
          role
        }
      }),
      invalidatesTags: ['airlineAdmin']
    })
  })
});

export const {
  useGetOnboardingUsersQuery,
  useGetOnboardingUsersProfileQuery,
  useGetRosterDataMutation,
  useGetOnboardingUserAirlineMutation,
  useTriggerAppFailureMutation,
  useLazyGetAppFailureStatusQuery,
  useTriggerSitmapFailureMutation,
  useTriggerRosterFailureMutation,
  useTriggerFlightPlanFailureMutation,
  useGetFlightPlanInfoExtractQuery,
  useGetAirlineUsersQuery,
  useCreateAirlineUserMutation,
  useDeleteAirlineUserMutation,
  useGetAirlineUserProfileQuery,
  useUpdateAirlineUserProfileMutation,
  useUpdateAirlineUserBlockStatusMutation
} = airlineAdminApi;
